import React, { useCallback, useEffect } from "react";
import { Tooltip } from "antd";
import "./TopFilters.css";
import { CombinedConceptSelector, ConceptsSelector } from "../../DashboardComponents/DashboardFilters/shared";
import FontSizeManager from "../FontSizeManager/FontSizeManager";
import Scrollbars from "react-custom-scrollbars-2";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
    setActiveConceptList,
    setDeselection,
    setDeselectionConcepts,
    setDeselectionDate,
    setSelections,
    setTopConceptList,
    setTopConceptListDaylight,
    setTopConcepts,
    setTopFilters,
} from "../../../store/slices/dashboardSlice";
import { FilterItem } from "./TopItemFilter/FilterItem";
import { selectDashboardById } from "../../../store/selectors/selectors";

const TopFilters = ({
                        metadata,
                        isOpen,
                        dataList,
                    }) => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const {
        topFilters = [],
        selections = {},
        topConcepts = [],
        activeConceptList = "",
    } = useSelector((state) => selectDashboardById(state, id));

    const { topOptionsConcepts, sentimentDisabled } = useSelector((state) => state.project);

    const dispatchSetFilters = useCallback((filterData) => {
        dispatch(setTopFilters({ id, filters: [filterData] }));
    }, [dispatch, id]);

    const dispatchSetSelections = useCallback((selectionData) => {
        dispatch(setSelections({ id, selections: selectionData }));
    }, [dispatch, id]);

    const dispatchSetTopConcepts = useCallback((concepts) => {
        dispatch(setTopConcepts({ id, concepts }));
    }, [dispatch, id]);

    const handleConceptsChange = useCallback(
        (newConcepts) => {
            dispatchSetTopConcepts(newConcepts);
        },
        [dispatchSetTopConcepts],
    );

    const dispatchSetDeselection = useCallback((deselection) => {
        dispatch(setDeselection({ id, deselection }));
    }, [dispatch, id]);

    const dispatchSetDeselectionConcepts = (deselectionConcepts) => {
        dispatch(setDeselectionConcepts({ id, deselectionConcepts }));
    };
    const handleConceptListChange = useCallback(
        (list) => {
            dispatch(setTopConceptList({ id, list }));
            dispatch(setActiveConceptList({ id, list }));
        },
        [dispatch, id],
    );

    const handleChangeType = useCallback(
        (type) => {
            dispatch(setTopConceptListDaylight({ id, daylight: type }));
            dispatch(setActiveConceptList({ id, list: `Type: ${type}` }));
        },
        [dispatch, id],
    );



    const handleFilter = useCallback((item, newValue) => {
        const previousValues = selections[item.name]?.values || [];
        const deselectedValues = previousValues.filter(
            (x) => !newValue.includes(x),
        );

        if (deselectedValues.length > 0) {
            dispatchSetDeselection({
                name: item.name,
                values: deselectedValues,
                type: item.type,
            });
        }

        dispatchSetSelections({
            [item.name]: {
                type: item.type,
                values: newValue,
            },
        });
    }, [dispatchSetSelections, selections, dispatchSetDeselection]);

    const dispatchSetDeselectionDate = (deselectionDate) => {
        dispatch(setDeselectionDate({ id, deselectionDate }));
    };

    const handleDateDeselection = (dateField) => {
        dispatchSetDeselectionDate(dateField);
    };

    const dateMetadata = metadata?.metaDataDate?.map((el) => el.name, []);
    useEffect(() => {
        const filterArray = Object?.entries(selections).reduce(
            (acc, [name, { type, values = [] }]) => {
                if (type === "number" && values.length > 0) {
                    const numValues = values.map(Number);
                    acc.push({
                        name,
                        minimum: Math.min(...numValues),
                        maximum: Math.max(...numValues),
                    });
                } else if (type === "date" && values.minimum && values.maximum) {
                    acc.push({
                        name,
                        minimum: new Date(values.minimum).toISOString(),
                        maximum: new Date(values.maximum).toISOString(),
                    });
                } else if (values.length > 0) {
                    acc.push({ name, values });
                }
                return acc;
            },
            [],
        );
        if (topFilters) {
            topFilters.forEach((filter) => {
                if (dateMetadata.includes(filter.name)) {
                    filterArray.push(filter);
                }
            });
        }
        const areFiltersEqual = JSON.stringify(filterArray) === JSON.stringify(topFilters);
        if (!areFiltersEqual) {
            dispatch(setTopFilters({ id, filters: filterArray }));
        }
    }, [selections, topFilters, dateMetadata, dispatch, id]);

    return (
        <>
            {isOpen && (
                <div className="top-filters-container" id="topFilterContainer">
                    <Scrollbars
                        autoHeight
                        autoHeightMin={100}
                        autoHeightMax={190}
                        renderView={props => (
                            <div {...props} style={{ ...props.style, overflowX: "hidden" }} />
                        )}
                        style={{ overflowX: "hidden" }}
                    >
                        <div
                            className={"top-filters-scroll"}
                        >
                            {metadata?.metaData.map((item) => (
                                <FilterItem
                                    key={item.name}
                                    topFilters={topFilters}
                                    item={item}
                                    handleFilter={handleFilter}
                                    selections={selections}
                                    setTopFilters={(filters) => {
                                        const newFilters = Array.isArray(filters) ? filters : [filters];
                                        dispatchSetFilters(
                                            newFilters.map((filter) => ({
                                                name: filter.name,
                                                minimum: filter?.minimum?.toISOString(),
                                                maximum: filter?.maximum?.toISOString(),
                                            })),
                                        );
                                    }}
                                    setDeselectionDate={handleDateDeselection}
                                />
                            ))}
                        </div>
                    </Scrollbars>

                    <div className={"top-filters-column"}>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <ConceptsFilter
                                label="Concepts"
                                size="middle"
                                width={100}
                                dropdownStyle={{
                                    width: "fit-content",
                                    minWidth: "150px",
                                }}
                                localConceptOptions={topOptionsConcepts}
                                selectedLocalConcepts={topConcepts}
                                setConceptList={handleConceptListChange}
                                setSelectedLocalConcepts={handleConceptsChange}
                                deselect={true}
                                setDeselectionConcepts={dispatchSetDeselectionConcepts}
                            />
                            <div>
                                <CombinedConceptSelectorFilter
                                    label="Concept Lists"
                                    size={"middle"}
                                    width={100}
                                    dropdownStyle={{
                                        width: "fit-content",
                                        minWidth: "150px",
                                    }}
                                    sentimentDisabled={sentimentDisabled}
                                    conceptLists={dataList}
                                    conceptList={activeConceptList}
                                    setConceptList={handleConceptListChange}
                                    conceptTypeDay={activeConceptList}
                                    handleChangeType={handleChangeType}
                                    setSelectedLocalConcepts={handleConceptsChange}
                                    resetType={() => dispatch(setTopConceptListDaylight(""))}
                                />
                            </div>
                        </div>
                        <FontSizeManager />
                    </div>
                </div>
            )}
        </>
    );
};

const ConceptsFilter = ({ label, ...props }) => (
    <div className="filter-item">
        <Tooltip title={label} placement="topLeft">
            <label>{label}</label>
        </Tooltip>
        <ConceptsSelector {...props} />
    </div>
);

const CombinedConceptSelectorFilter = ({ label, ...props }) => (
    <div className="filter-item">
        <Tooltip title={label} placement="topLeft">
            <label>{label}</label>
        </Tooltip>
        <CombinedConceptSelector {...props} />

    </div>
);

export default TopFilters;
